import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Dialog, DialogContent, Typography, TextField, Button, Chip,
  IconButton, useTheme, LinearProgress, Select, MenuItem, FormControl, Autocomplete,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import config from '../config';

// Shared shape with PeoplePage.tsx / PropertiesPage.tsx's own Owner interface — see
// backend/models.py's Owner model for the full field list this mirrors.
export interface Owner {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  entity_type: 'individual' | 'business';
  is_owner: boolean;
  is_manager: boolean;
  country: string | null;
  vat_number: string | null;
  created_at: string | null;
}

// Same two one-click presets used everywhere else in the app (PropertiesPage.tsx) — kept as a
// smaller local copy rather than importing from there, since that file doesn't export it and a
// wizard-only country field doesn't need the full geocoding machinery that lives alongside it.
const COUNTRY_PRESETS = ['United Kingdom', 'Cyprus'];
const WORLD_COUNTRIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina',
  'Armenia', 'Australia', 'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados',
  'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina',
  'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi', 'Cabo Verde', 'Cambodia',
  'Cameroon', 'Canada', 'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia',
  'Comoros', 'Congo (Congo-Brazzaville)', 'Costa Rica', 'Croatia', 'Cuba', 'Czech Republic',
  'Democratic Republic of the Congo', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic',
  'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini',
  'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana',
  'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana', 'Haiti', 'Honduras',
  'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy',
  'Ivory Coast', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati', 'Kosovo',
  'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya',
  'Liechtenstein', 'Lithuania', 'Luxembourg', 'Madagascar', 'Malawi', 'Malaysia', 'Maldives',
  'Mali', 'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico', 'Micronesia',
  'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar', 'Namibia',
  'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Korea',
  'North Macedonia', 'Norway', 'Oman', 'Pakistan', 'Palau', 'Palestine', 'Panama',
  'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania',
  'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines',
  'Samoa', 'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Senegal', 'Serbia',
  'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia',
  'South Africa', 'South Korea', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname',
  'Sweden', 'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand',
  'Timor-Leste', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan',
  'Tuvalu', 'Uganda', 'Ukraine', 'United Arab Emirates', 'United States', 'Uruguay', 'Uzbekistan',
  'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe',
];

interface WizardForm {
  entity_type: 'individual' | 'business';
  name: string;
  is_owner: boolean;
  is_manager: boolean;
  country: string;
  email: string;
  phone: string;
  vat_number: string;
  connect_bank_now: boolean | null; // null = not yet answered
}

const emptyForm = (): WizardForm => ({
  entity_type: 'individual',
  name: '',
  is_owner: true,
  is_manager: false,
  country: '',
  email: '',
  phone: '',
  vat_number: '',
  connect_bank_now: null,
});

// Each step is asked one at a time, in a single "question card" — see DESIGN note in
// PeoplePage.tsx for why (replaces the old single-form "New profile" dialog). `vat` only
// appears when entity_type === 'business' (computed in the `steps` memo below, not listed here).
type StepKind = 'entity_type' | 'name' | 'role' | 'country' | 'email' | 'phone' | 'vat' | 'bank';

interface ProfileWizardProps {
  open: boolean;
  onClose: () => void;
  onSaved: (owner: Owner, connectBankNow: boolean) => void;
  editTarget: Owner | null;
  suggestedIndividualName: string;
  suggestedEmail: string;
  token: string | null;
}

const ProfileWizard: React.FC<ProfileWizardProps> = ({ open, onClose, onSaved, editTarget, suggestedIndividualName, suggestedEmail, token }) => {
  const { palette } = useTheme();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<WizardForm>(emptyForm());
  const [countryMode, setCountryMode] = useState<'preset' | 'other'>('preset');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (editTarget) {
      setForm({
        entity_type: editTarget.entity_type,
        name: editTarget.name,
        is_owner: editTarget.is_owner,
        is_manager: editTarget.is_manager,
        country: editTarget.country || '',
        email: editTarget.email || '',
        phone: editTarget.phone || '',
        vat_number: editTarget.vat_number || '',
        // Editing an existing profile never re-asks about connecting a bank — that's a separate,
        // already-built flow (Accounting's "Manage banks" owner assignment).
        connect_bank_now: false,
      });
      setCountryMode(editTarget.country && !COUNTRY_PRESETS.includes(editTarget.country) ? 'other' : 'preset');
    } else {
      setForm(emptyForm());
      setCountryMode('preset');
    }
    setStep(0);
    setError(null);
  }, [open, editTarget]);

  const steps: StepKind[] = useMemo(() => {
    const base: StepKind[] = ['entity_type', 'name', 'role', 'country', 'email', 'phone'];
    if (form.entity_type === 'business') base.push('vat');
    if (!editTarget) base.push('bank');
    return base;
  }, [form.entity_type, editTarget]);

  const kind = steps[step];

  const suggestedName = form.entity_type === 'individual'
    ? (suggestedIndividualName || 'e.g. Victor Melatti')
    : 'e.g. Riverside Estates Ltd';
  const emailPlaceholder = suggestedEmail || 'e.g. name@example.com';

  const isStepValid = (k: StepKind): boolean => {
    switch (k) {
      case 'entity_type': return !!form.entity_type;
      case 'name': return form.name.trim().length > 0;
      case 'role': return form.is_owner || form.is_manager;
      case 'bank': return form.connect_bank_now !== null;
      default: return true; // country/email/phone/vat are all optional
    }
  };

  const canSkip = (k: StepKind) => ['country', 'email', 'phone', 'vat'].includes(k);

  const handleBack = () => {
    setError(null);
    setStep(s => Math.max(0, s - 1));
  };

  const handleSkip = () => setStep(s => Math.min(steps.length - 1, s + 1));

  const handleNext = async () => {
    if (!isStepValid(kind)) return;
    if (step < steps.length - 1) {
      setStep(s => s + 1);
      return;
    }
    // Last step reached — save.
    setSaving(true);
    setError(null);
    try {
      const body = {
        name: form.name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        entity_type: form.entity_type,
        is_owner: form.is_owner,
        is_manager: form.is_manager,
        country: form.country || null,
        vat_number: form.entity_type === 'business' ? (form.vat_number.trim() || null) : null,
      };
      const url = editTarget
        ? `${config.apiUrl}/api/accounting/owners/${editTarget.id}`
        : `${config.apiUrl}/api/accounting/owners`;
      const res = await fetch(url, {
        method: editTarget ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to save profile');
      onSaved(data.owner, !!form.connect_bank_now);
    } catch (err: any) {
      setError(err.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleNext();
    }
  };

  const roleLabel = form.name.trim() || (form.entity_type === 'individual' ? 'this person' : 'this business');

  const questionText: Record<StepKind, string> = {
    entity_type: 'Are you adding a profile for an individual or a business/company?',
    name: 'What is their name?',
    role: `Is ${roleLabel} an owner of any properties in this portfolio, a manager, or both?`,
    country: 'Which country are they based in?',
    email: 'What is their contact email?',
    phone: 'What is their contact phone number?',
    vat: 'What is their VAT number?',
    bank: 'Would you like to connect a bank account for this profile now?',
  };

  const helperText: Record<StepKind, string> = {
    entity_type: 'Individual is a single person, like yourself or a family member. Business/company covers companies, letting agencies, or anything trading as an Ltd.',
    name: 'This is the name shown whenever you assign ownership or management to a property.',
    role: 'Owner is who the property legally belongs to. Manager is who runs it day to day (e.g. a letting agent). The same person or company can be both.',
    country: 'Optional — used for tax/VAT context. You can skip this and add it later.',
    email: 'Optional contact email for this profile. You can skip this and add it later.',
    phone: 'Optional contact phone number for this profile. You can skip this and add it later.',
    vat: 'Only relevant for VAT-registered businesses. Skip if not applicable.',
    bank: 'Connecting now scopes that bank\'s transactions to this profile automatically. You can always do this later from Accounting instead.',
  };

  const renderStepContent = () => {
    switch (kind) {
      case 'entity_type':
        return (
          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            {(['individual', 'business'] as const).map(opt => (
              <Box
                key={opt}
                onClick={() => setForm(f => ({ ...f, entity_type: opt }))}
                sx={{
                  flex: 1, cursor: 'pointer', borderRadius: 3, p: 3, textAlign: 'center',
                  border: `2px solid ${form.entity_type === opt ? palette.tokens.primary : palette.tokens.hairline}`,
                  bgcolor: form.entity_type === opt ? palette.tokens.surfaceSoft : palette.tokens.white,
                  transition: 'all 0.15s',
                }}
              >
                {opt === 'individual' ? <PersonIcon sx={{ fontSize: 32, color: palette.tokens.primary, mb: 1 }} /> : <BusinessIcon sx={{ fontSize: 32, color: palette.tokens.primary, mb: 1 }} />}
                <Typography sx={{ fontWeight: 700, color: palette.tokens.ink, textTransform: 'capitalize' }}>{opt}</Typography>
              </Box>
            ))}
          </Box>
        );
      case 'name':
        return (
          <Box sx={{ mt: 3 }}>
            <TextField
              autoFocus fullWidth value={form.name} placeholder={suggestedName}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              onKeyDown={handleKeyDown}
            />
            {!form.name && (
              <Chip
                size="small" label={`Use suggestion: "${suggestedName.replace(/^e\.g\. /, '')}"`}
                onClick={() => setForm(f => ({ ...f, name: suggestedName.replace(/^e\.g\. /, '') }))}
                sx={{ mt: 1.5 }}
              />
            )}
          </Box>
        );
      case 'role':
        return (
          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            {([
              { label: 'Owner only', is_owner: true, is_manager: false },
              { label: 'Manager only', is_owner: false, is_manager: true },
              { label: 'Both', is_owner: true, is_manager: true },
            ] as const).map(opt => {
              const selected = form.is_owner === opt.is_owner && form.is_manager === opt.is_manager;
              return (
                <Box
                  key={opt.label}
                  onClick={() => setForm(f => ({ ...f, is_owner: opt.is_owner, is_manager: opt.is_manager }))}
                  sx={{
                    flex: 1, cursor: 'pointer', borderRadius: 3, p: 2.5, textAlign: 'center',
                    border: `2px solid ${selected ? palette.tokens.primary : palette.tokens.hairline}`,
                    bgcolor: selected ? palette.tokens.surfaceSoft : palette.tokens.white,
                  }}
                >
                  <Typography sx={{ fontWeight: 700, color: palette.tokens.ink }}>{opt.label}</Typography>
                </Box>
              );
            })}
          </Box>
        );
      case 'country':
        return (
          <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <FormControl fullWidth>
              <Select
                value={countryMode === 'other' ? 'Other' : form.country}
                displayEmpty
                onChange={e => {
                  const v = e.target.value;
                  if (v === 'Other') { setCountryMode('other'); setForm(f => ({ ...f, country: '' })); }
                  else { setCountryMode('preset'); setForm(f => ({ ...f, country: v as string })); }
                }}
                renderValue={(val) => !val
                  ? <Typography sx={{ color: palette.tokens.mutedSoft }}>Not set</Typography>
                  : (countryMode === 'other' ? 'Other' : val as string)}
              >
                <MenuItem value=""><em>Not set</em></MenuItem>
                {COUNTRY_PRESETS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>
            {countryMode === 'other' && (
              <Autocomplete
                options={WORLD_COUNTRIES}
                value={WORLD_COUNTRIES.includes(form.country) ? form.country : null}
                onChange={(_, v) => setForm(f => ({ ...f, country: v || '' }))}
                renderInput={(params) => <TextField {...params} autoFocus placeholder="Search for a country" onKeyDown={handleKeyDown} />}
              />
            )}
          </Box>
        );
      case 'email':
        return (
          <Box sx={{ mt: 3 }}>
            <TextField
              autoFocus fullWidth type="email" placeholder={emailPlaceholder}
              value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              onKeyDown={handleKeyDown}
            />
            {!form.email && suggestedEmail && (
              <Chip
                size="small" label={`Use my email: "${suggestedEmail}"`}
                onClick={() => setForm(f => ({ ...f, email: suggestedEmail }))}
                sx={{ mt: 1.5 }}
              />
            )}
          </Box>
        );
      case 'phone':
        return (
          <TextField
            autoFocus fullWidth sx={{ mt: 3 }} placeholder="e.g. +44 7700 900000"
            value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
            onKeyDown={handleKeyDown}
          />
        );
      case 'vat':
        return (
          <TextField
            autoFocus fullWidth sx={{ mt: 3 }} placeholder="e.g. GB123456789"
            value={form.vat_number} onChange={e => setForm(f => ({ ...f, vat_number: e.target.value }))}
            onKeyDown={handleKeyDown}
          />
        );
      case 'bank':
        return (
          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            {([
              { label: 'Connect a bank now', value: true },
              { label: "I'll do it later", value: false },
            ] as const).map(opt => (
              <Box
                key={opt.label}
                onClick={() => setForm(f => ({ ...f, connect_bank_now: opt.value }))}
                sx={{
                  flex: 1, cursor: 'pointer', borderRadius: 3, p: 2.5, textAlign: 'center',
                  border: `2px solid ${form.connect_bank_now === opt.value ? palette.tokens.primary : palette.tokens.hairline}`,
                  bgcolor: form.connect_bank_now === opt.value ? palette.tokens.surfaceSoft : palette.tokens.white,
                }}
              >
                <Typography sx={{ fontWeight: 700, color: palette.tokens.ink }}>{opt.label}</Typography>
              </Box>
            ))}
          </Box>
        );
      default:
        return null;
    }
  };

  const isLastStep = step === steps.length - 1;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <LinearProgress variant="determinate" value={((step + 1) / steps.length) * 100} sx={{ height: 4 }} />
      <DialogContent sx={{ p: 4 }} onKeyDown={handleKeyDown}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: palette.tokens.mutedSoft, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {editTarget ? 'Edit profile' : 'New profile'} · Step {step + 1} of {steps.length}
          </Typography>
          <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        </Box>

        <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: palette.tokens.ink, mt: 1 }}>
          {questionText[kind]}
        </Typography>
        <Typography sx={{ fontSize: '0.9rem', color: palette.tokens.muted, mt: 0.75 }}>
          {helperText[kind]}
        </Typography>

        {renderStepContent()}

        {error && (
          <Typography sx={{ color: palette.tokens.error, fontSize: '0.85rem', mt: 2 }}>{error}</Typography>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />} disabled={step === 0 || saving}
            onClick={handleBack} sx={{ color: palette.tokens.muted }}
          >
            Back
          </Button>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {canSkip(kind) && (
              <Button disabled={saving} onClick={handleSkip} sx={{ color: palette.tokens.muted }}>
                Skip
              </Button>
            )}
            <Button
              variant="contained" disabled={!isStepValid(kind) || saving}
              onClick={handleNext} sx={{ borderRadius: 2, px: 3 }}
            >
              {saving ? 'Saving…' : isLastStep ? 'Finish' : 'Continue'}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileWizard;
